<template>
  <div class="media-devices-settings">
    <button
      aria-label="Close"
      class="media-devices-settings__close attest-button attest-button--styleless"
      data-test-id="MediaDevicesSettings_close"
      @click="$emit('close')"
    >
      <img src="../../assets/close.svg" />
    </button>
    <div class="media-devices-settings__container">
      <h2 class="media-devices-settings__title">
        {{ c('media_device_settings.title') }}
      </h2>

      <div
        v-for="setting of settings"
        :key="setting.title"
        class="media-devices-settings__device-selection"
      >
        <h3 class="media-devices-settings__device-selection-heading">
          {{ setting.title }}
        </h3>
        <select
          v-model="selectModels[setting.type]"
          class="attest-select"
          :data-test-id="`MediaDevicesSettings_DeviceSelect-${setting.type}`"
        >
          <option v-for="option of setting.options" :key="option.deviceId" :value="option.deviceId">
            {{ option.label }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, reactive, type WritableComputedRef } from 'vue'

import { c } from '../../../intl'
import { useMediaDevices } from '../../../shared/composables/useMediaDevices'

export default defineComponent({
  props: {
    selectedDeviceIds: {
      type: Object as PropType<{
        video?: string
        audio?: string
      }>,
      required: true,
    },
  },
  emits: {
    close: () => true,
    change: (_selected: { video?: string; audio?: string }) => true,
  },

  setup(props, { emit }) {
    const mediaDevices = useMediaDevices()

    const createSelectModelFor = (
      type: 'audio' | 'video',
    ): WritableComputedRef<string | undefined> =>
      computed({
        get: () => props.selectedDeviceIds[type],
        set: (id) => emit('change', { ...props.selectedDeviceIds, [type]: id }),
      })

    return reactive({
      settings: [
        {
          type: 'video' as const,
          title: c('media_device_settings.camera.title'),
          options: computed(
            () =>
              mediaDevices.value
                ?.filter(({ kind }) => kind === 'videoinput')
                .map(({ label, deviceId }) => ({ label, deviceId })) ?? [],
          ),
        },
        {
          type: 'audio' as const,
          title: c('media_device_settings.about.title'),
          options: computed(
            () =>
              mediaDevices.value
                ?.filter(({ kind }) => kind === 'audioinput')
                .map(({ label, deviceId }) => ({ label, deviceId })) ?? [],
          ),
        },
      ],
      selectModels: {
        video: createSelectModelFor('video'),
        audio: createSelectModelFor('audio'),
      },
      c,
    })
  },
})
</script>

<style lang="postcss">
.media-devices-settings {
  --media-devices-settings-padding: var(--attest-spacing-7);
  background-color: #464646;
  display: flex;
  color: white;
  height: 100%;
  justify-content: center;
  align-items: center;
  padding: 0 var(--media-devices-settings-padding);
  position: relative;
  font-size: var(--attest-font-size-m);
  &__close {
    top: var(--media-devices-settings-padding);
    right: var(--media-devices-settings-padding);
    position: absolute;
    width: unset;
  }

  &__title {
    font-size: var(--attest-font-size-l);
  }

  &__container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__device-selection {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    &-heading {
      font-size: var(--attest-font-size-m);
    }
  }
}
</style>
